import { Dialog, FormGroup, styled } from '@mui/material';
import { useUser } from '@/contexts/user/UserContext';
import { useEffect, useState } from 'react';

import { TermsVersionsResponse } from '@/models/payment';
import { UpdateConsentsRequest, UserRoles } from '@/models/company';
import { ConsentFormActions } from './ConsentFormActions';
import { ConsentCheckbox } from './ConsentCheckbox';
import { Regular, SemiBold, SMRegular } from '@/typography/Typography';
import {
    ConsentStyledDialog,
    StyledDialog,
    StyledDialogTitle,
    StyledDynamicContentBox
} from './StyledDialog';
import { useFormatting } from '@/utils/formatting';
import { Style } from '@mui/icons-material';

export type FirstTimeConsentProps = {
    termsVersions: TermsVersionsResponse;
    updateConsents: (request: UpdateConsentsRequest) => void;
    postponeConsents: () => void;
};

export const FirstTimeConsent = (props: FirstTimeConsentProps) => {
    const { user, company } = useUser();
    const { formatDate } = useFormatting();
    const [commercialAndDataConsent, setCommercialAndDataConsent] =
        useState(false);
    const [newsletterConsent, setNewsletterConsent] = useState(false);
    const commercialTerms = props.termsVersions.termsVersions.find(
        (_) => _.consentType === 'Commercial'
    );
    const dataTerms = props.termsVersions.termsVersions.find(
        (_) => _.consentType === 'Data'
    );

    return (
        <ConsentStyledDialog
            open={true}
            fullWidth={true}
            maxWidth="sm"
            slotProps={{
                paper: {
                    style: {
                        borderRadius: '12px',
                        boxShadow:
                            '0px 8px 8px -4px #10182808, 0px 20px 24px -4px #10182814',
                        padding: '28px 24px 24px 24px'
                    }
                }
            }}
        >
            <StyledDialogTitle
                style={{
                    paddingTop: '16px',
                    width: '664px'
                }}
            >
                <img
                    style={{
                        marginBottom: '16px'
                    }}
                    src="/consent_top_logo.svg"
                    alt="InstaPaid Logo"
                    width={150}
                    height={50}
                />
                <SemiBold>
                    {' '}
                    Hi {user.firstName}. Welcome to the InstaPaid
                    BackOffice{' '}
                </SemiBold>
            </StyledDialogTitle>
            <div>
                <StyledDynamicContentBox>
                    <SMRegular>
                        We&apos;re thrilled to welcome you and {company.name}.
                    </SMRegular>

                    <SMRegular>
                        This is your company&apos;s dashboard, where you&apos;ll
                        have access to our services, tailored to fit your needs.
                    </SMRegular>

                    {company.endOfTrial !== null && (
                        <p>
                            <b>
                                <SMRegular>
                                    We&apos;re excited to present an exclusive
                                    introductory offer, allowing you to enjoy
                                    and assess our services entirely free of
                                    charge and without commitment until{' '}
                                    {formatDate(company.endOfTrial)}.
                                </SMRegular>
                            </b>
                        </p>
                    )}
                    <p>
                        <SMRegular>
                            If you have any questions or need assistance, our
                            dedicated support team is always ready to help.
                            Don&apos;t hesitate to reach out.
                        </SMRegular>
                    </p>

                    <p>
                        <SMRegular>
                            Thank you for choosing InstaPaid. Let&apos;s embark
                            on this exciting journey together!
                        </SMRegular>
                    </p>

                    <br />
                    <SMRegular> Warm Regards</SMRegular>

                    <p>
                        <Regular> Michael Kayser</Regular>
                        CEO
                    </p>
                </StyledDynamicContentBox>

                <FormGroup
                    style={{
                        margin: '16px 24px 8px 0px'
                    }}
                >
                    <ConsentCheckbox
                        checked={commercialAndDataConsent}
                        setChecked={(checked) =>
                            setCommercialAndDataConsent(checked)
                        }
                        label={
                            <SMRegular>
                                <span>I have read and accepted the </span>
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={commercialTerms?.url}
                                >
                                    license agreement
                                </a>
                                <span> and the </span>
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href={dataTerms?.url}
                                >
                                    data processing agreement
                                </a>
                                .
                            </SMRegular>
                        }
                    />
                    <ConsentCheckbox
                        checked={newsletterConsent}
                        setChecked={(checked) => setNewsletterConsent(checked)}
                        label={
                            <SMRegular>
                                I hereby give my consent to receive newsletters
                                and information from InstaPaid via email or
                                through the InstaPaid application regarding
                                InstaPaid&apos;s product range, such as updates
                                and new features in the InstaPaid application.
                                InstaPaid will process my information, including
                                name, contact details, and company, for this
                                purpose.
                            </SMRegular>
                        }
                    />
                </FormGroup>
            </div>

            <ConsentFormActions
                commercialConsent={commercialAndDataConsent}
                dataConsent={commercialAndDataConsent}
                newsletterConsent={newsletterConsent}
                postponeConsents={props.postponeConsents}
                updateConsents={props.updateConsents}
                termsVersions={props.termsVersions}
            />
        </ConsentStyledDialog>
    );
};
